import { useEffect, useRef, useState, useCallback } from 'react';
import webSocketService from '../services/webSocketService.js';
import { useAuth } from '../contexts/AuthContext.js';
import { API_CONFIG } from '../config/api.js';

export const useWebSocket = () => {
  const { user, isAuthenticated } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    reconnectAttempts: 0,
    socketId: null
  });
  const [lastError, setLastError] = useState(null);
  const eventListenersRef = useRef(new Map());
  // Initialize WebSocket connection
  useEffect(() => {
    if (isAuthenticated && user) {
      const token = localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      if (token) {
        webSocketService.connect(token);
      } else {
        console.error('No JWT token found for WebSocket connection');
      }
    }
    return () => {
      // Clean up event listeners on unmount
      eventListenersRef.current.forEach((callback, event) => {
        webSocketService.off(event, callback);
      });
      eventListenersRef.current.clear();
    };
  }, [isAuthenticated, user]);
  // Monitor connection status
  useEffect(() => {
    const handleConnectionStatus = (data) => {
      setConnectionStatus(webSocketService.getConnectionStatus());
      if (!data.connected) {
        setLastError(data.reason || 'Connection lost');
      } else {
        setLastError(null);
      }
    };
    const handleConnectionError = (error) => {
      setLastError(error.message || 'Connection error');
      setConnectionStatus(webSocketService.getConnectionStatus());
    };
    const handleConnectionFailed = (data) => {
      setLastError(data.message);
      setConnectionStatus(webSocketService.getConnectionStatus());
    };
    webSocketService.on('connection_status', handleConnectionStatus);
    webSocketService.on('connection_error', handleConnectionError);
    webSocketService.on('connection_failed', handleConnectionFailed);
    // Store listeners for cleanup
    eventListenersRef.current.set('connection_status', handleConnectionStatus);
    eventListenersRef.current.set('connection_error', handleConnectionError);
    eventListenersRef.current.set('connection_failed', handleConnectionFailed);
    // Update connection status periodically
    const interval = setInterval(() => {
      setConnectionStatus(webSocketService.getConnectionStatus());
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, []);
  // Join booking room
  const joinBookingRoom = useCallback((bookingId) => {
    webSocketService.joinBookingRoom(bookingId);
  }, []);
  // Leave booking room
  const leaveBookingRoom = useCallback((bookingId) => {
    webSocketService.leaveBookingRoom(bookingId);
  }, []);
  // Send location update
  const sendLocationUpdate = useCallback((data) => {
    webSocketService.sendLocationUpdate(data);
  }, []);
  // Send booking status update
  const sendBookingStatusUpdate = useCallback((data) => {
    webSocketService.sendBookingStatusUpdate(data);
  }, []);
  // Reconnect WebSocket
  const reconnect = useCallback(() => {
    webSocketService.reconnect();
  }, []);
  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    webSocketService.disconnect();
  }, []);
  return {
    connectionStatus,
    lastError,
    joinBookingRoom,
    leaveBookingRoom,
    sendLocationUpdate,
    sendBookingStatusUpdate,
    reconnect,
    disconnect,
    isConnected: connectionStatus.connected
  };
};
export const useBookingWebSocket = (bookingId) => {
  const [bookingUpdates, setBookingUpdates] = useState([]);
  const [locationUpdates, setLocationUpdates] = useState([]);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  const eventListenersRef = useRef(new Map());
  // Join booking room when bookingId is provided
  useEffect(() => {
    if (bookingId) {
      webSocketService.joinBookingRoom(bookingId);
      setIsTracking(true);
      return () => {
        webSocketService.leaveBookingRoom(bookingId);
        setIsTracking(false);
      };
    }
  }, [bookingId]);
  // Set up event listeners for booking-specific events
  useEffect(() => {
    if (!bookingId) return;
    const handleLocationUpdate = (data) => {
      if (data.bookingId === bookingId) {
        setLocationUpdates(prev => [...prev, data]);
      }
    };
    const handleStatusUpdate = (data) => {
      if (data.bookingId === bookingId) {
        setStatusUpdates(prev => [...prev, data]);
        setBookingUpdates(prev => [...prev, data]);
      }
    };
    const handleBookingCancelled = (data) => {
      if (data.bookingId === bookingId) {
        setBookingUpdates(prev => [...prev, data]);
      }
    };
    const handleUserJoined = (data) => {
      setBookingUpdates(prev => [...prev, {
        type: 'user_joined',
        ...data,
        timestamp: new Date().toISOString()
      }]);
    };
    webSocketService.on('location_update', handleLocationUpdate);
    webSocketService.on('booking_status_update', handleStatusUpdate);
    webSocketService.on('booking_cancelled', handleBookingCancelled);
    webSocketService.on('user_joined', handleUserJoined);
    // Store listeners for cleanup
    eventListenersRef.current.set('location_update', handleLocationUpdate);
    eventListenersRef.current.set('booking_status_update', handleStatusUpdate);
    eventListenersRef.current.set('booking_cancelled', handleBookingCancelled);
    eventListenersRef.current.set('user_joined', handleUserJoined);
    return () => {
      // Clean up event listeners
      eventListenersRef.current.forEach((callback, event) => {
        webSocketService.off(event, callback);
      });
      eventListenersRef.current.clear();
    };
  }, [bookingId]);
  // Get latest location
  const getLatestLocation = useCallback(() => {
    return locationUpdates[locationUpdates.length - 1] || null;
  }, [locationUpdates]);
  // Get latest status
  const getLatestStatus = useCallback(() => {
    return statusUpdates[statusUpdates.length - 1] || null;
  }, [statusUpdates]);
  // Clear updates
  const clearUpdates = useCallback(() => {
    setBookingUpdates([]);
    setLocationUpdates([]);
    setStatusUpdates([]);
  }, []);
  return {
    bookingUpdates,
    locationUpdates,
    statusUpdates,
    isTracking,
    getLatestLocation,
    getLatestStatus,
    clearUpdates
  };
};
export const useLiveTracking = (bookingId) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [route, setRoute] = useState([]);
  const [eta, setEta] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const eventListenersRef = useRef(new Map());
  // Start tracking when bookingId is provided
  useEffect(() => {
    if (bookingId) {
      webSocketService.joinBookingRoom(bookingId);
      setIsTracking(true);
      return () => {
        webSocketService.leaveBookingRoom(bookingId);
        setIsTracking(false);
      };
    }
  }, [bookingId]);
  // Listen for location updates
  useEffect(() => {
    if (!bookingId) return;
    const handleLocationUpdate = (data) => {
      if (data.bookingId === bookingId) {
        setCurrentLocation({
          latitude: data.latitude,
          longitude: data.longitude,
          address: data.address,
          heading: data.heading,
          speed: data.speed,
          timestamp: data.timestamp
        });
        // Add to route history
        setRoute(prev => [...prev, {
          latitude: data.latitude,
          longitude: data.longitude,
          timestamp: data.timestamp
        }]);
        // Calculate ETA (mock implementation)
        if (data.speed && data.speed > 0) {
          const estimatedArrival = new Date(Date.now() + (30 * 60 * 1000)); // Mock 30 minutes
          setEta(estimatedArrival);
        }
      }
    };
    webSocketService.on('location_update', handleLocationUpdate);
    eventListenersRef.current.set('location_update', handleLocationUpdate);
    return () => {
      webSocketService.off('location_update', handleLocationUpdate);
      eventListenersRef.current.delete('location_update');
    };
  }, [bookingId]);
  // Clear tracking data
  const clearTracking = useCallback(() => {
    setCurrentLocation(null);
    setRoute([]);
    setEta(null);
  }, []);
  // Get distance to destination (mock implementation)
  const getDistanceToDestination = useCallback((destination) => {
    if (!currentLocation || !destination) return null;
    // Mock distance calculation
    const distance = Math.sqrt(
      Math.pow(destination.latitude - currentLocation.latitude, 2) +
      Math.pow(destination.longitude - currentLocation.longitude, 2)
    ) * 111; // Rough conversion to kilometers
    return Math.round(distance * 100) / 100;
  }, [currentLocation]);
  return {
    currentLocation,
    route,
    eta,
    isTracking,
    clearTracking,
    getDistanceToDestination
  };
};
